import { useState, useEffect, useMemo, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { UserButton, useUser } from '@clerk/clerk-react'
import { ArrowLeft, Trophy, Award, Users, TrendingUp, CheckCircle, X, Plus, Share2, Settings, Filter } from 'lucide-react'
import { useTeams } from '../hooks/useTeams'
import { useUserSpots } from '../hooks/useUserSpots'
import { EnhancedTeamBrowser } from '../components/team/enhanced-team-browser'
import { TeamDetailModal } from '../components/team/team-detail-modal'
import { TeamSuggestionDialog } from '../components/team/team-suggestion-dialog'
import { SpottedTeamCard } from '../components/team/CleanTeamCard'
import { ShareDialog } from '../components/ui/share-dialog'
import { Leaderboard } from '../components/ui/leaderboard'
import { MobileBottomNav } from '../components/ui/mobile-bottom-nav'
import { SwipeableCard } from '../components/ui/swipeable-card'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog'
import { useToast } from '../hooks/use-toast'
import { syncUserProfile } from '../lib/firestore'
import type { Team } from '../types'

export default function DashboardPage() {
  const navigate = useNavigate()
  const { user } = useUser()
  const [activeTab, setActiveTab] = useState<'your-teams' | 'browse' | 'leaderboard'>('browse')
  const [selectedTeam, setSelectedTeam] = useState<Team | null>(null)
  const [isTeamModalOpen, setIsTeamModalOpen] = useState(false)
  const [isTeamSuggestionOpen, setIsTeamSuggestionOpen] = useState(false)
  const [teamToRemove, setTeamToRemove] = useState<Team | null>(null)
  const [showResetConfirm, setShowResetConfirm] = useState(false)
  const [shareDialogOpen, setShareDialogOpen] = useState(false)
  const [spottedTeamsSortBy, setSpottedTeamsSortBy] = useState<'alphabetical' | 'league' | 'date'>('alphabetical')
  
  const { teams, loading, searchTeams } = useTeams()
  const { spottedTeams, spotTeam, removeSpot, trackingMode, switchTrackingMode, resetTripMode } = useUserSpots()
  const { toast } = useToast()

  // Sync user profile with Firestore when component loads
  useEffect(() => {
    if (user) {
      syncUserProfile(user)
    }
  }, [user])

  // Calculate stats
  const totalSpots = spottedTeams.length
  const uniqueLeagues = new Set(spottedTeams.map(team => team.league)).size
  const availableTeams = teams.length
  const progress = availableTeams > 0 ? Math.round((totalSpots / availableTeams) * 100) : 0

  // Sort spotted teams based on selected sorting option with memoization
  const sortedSpottedTeams = useMemo(() => {
    return [...spottedTeams].sort((a, b) => {
      switch (spottedTeamsSortBy) {
        case 'alphabetical':
          return (a.name || '').localeCompare(b.name || '')
        case 'league':
          // Sort by league first, then by name within league
          const leagueComparison = (a.league || '').localeCompare(b.league || '')
          return leagueComparison !== 0 ? leagueComparison : (a.name || '').localeCompare(b.name || '')
        case 'date':
          // Sort by spotted date (most recent first)
          const aDate = (a as any).spottedAt ? new Date((a as any).spottedAt) : new Date(0)
          const bDate = (b as any).spottedAt ? new Date((b as any).spottedAt) : new Date(0)
          return bDate.getTime() - aDate.getTime()
        default:
          return (a.name || '').localeCompare(b.name || '')
      }
    })
  }, [spottedTeams, spottedTeamsSortBy])
  
  const isTeamSpotted = (teamId: string) => {
    return spottedTeams.some(team => team.id === teamId)
  }

  const handleSpotTeam = async (team: Team) => {
    console.log('Spotting team:', team.name)
    const success = await spotTeam(team.id, team)
    if (success) {
      console.log(`Successfully spotted ${team.name}!`)
      toast({
        title: "Team Spotted!",
        description: `${team.name} has been added to your ${trackingMode === 'all-time' ? 'all-time' : 'trip'} spots.`,
        duration: 3000,
      })
    } else {
      toast({
        title: "Error",
        description: `Failed to spot ${team.name}. Please try again.`,
        variant: "destructive",
        duration: 3000,
      })
    }
  }

  const handleTeamClick = (team: Team) => {
    setSelectedTeam(team)
    setIsTeamModalOpen(true)
  }

  const closeTeamModal = () => {
    setIsTeamModalOpen(false)
    setSelectedTeam(null)
  }

  const handleRemoveSpot = (team: Team) => {
    setTeamToRemove(team)
  }

  const confirmRemoveSpot = async () => {
    if (!teamToRemove) return
    
    // Use the team's actual mode if available, otherwise use current tracking mode
    const teamMode = (teamToRemove as any).mode || trackingMode
    const success = await removeSpot(teamToRemove.id, teamMode)
    
    if (success) {
      toast({
        title: "Team Removed",
        description: `${teamToRemove.name} has been removed from your spotted teams.`,
        duration: 3000,
      })
    } else {
      toast({
        title: "Error",
        description: `Failed to remove ${teamToRemove.name}. Please try again.`,
        variant: "destructive",
        duration: 3000,
      })
    }
    
    setTeamToRemove(null)
  }

  const handleResetTrip = () => {
    setShowResetConfirm(true)
  }

  const confirmResetTrip = async () => {
    await resetTripMode()
    setShowResetConfirm(false)
  }

  const handleViewUser = (userId: string) => {
    navigate(`/user/${userId}/teams`)
  }




  return (
    <div className="min-h-screen bg-gray-50 pb-0 md:pb-0">
      {/* Mobile Bottom Navigation */}
      <MobileBottomNav
        activeTab={activeTab}
        onTabChange={setActiveTab}
        trackingMode={trackingMode}
        showLeaderboard={trackingMode === 'all-time'}
      />
      {/* Header - Optimized for Mobile (compressed height) */}
      <div className="bg-gradient-to-r from-[#42a5f5] to-[#1976d2] py-2 sm:py-6 shadow-lg">
        <div className="container mx-auto px-4">
          <div className="flex flex-col gap-2 sm:gap-4">
            {/* Mobile: Stack everything vertically, Desktop: Side by side */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-0">

              {/* Mode Toggle Buttons - Increased touch target to min 48px */}
              <div className="flex items-center justify-center sm:justify-start space-x-2 sm:space-x-3">
                <button
                  onClick={() => switchTrackingMode('all-time')}
                  className={`px-6 sm:px-4 min-h-[48px] py-3 sm:py-2 rounded-lg font-medium shadow-sm transition-colors text-sm touch-manipulation active:scale-95 flex-1 sm:flex-none ${
                    trackingMode === 'all-time'
                      ? 'bg-white text-[#42a5f5]'
                      : 'bg-[#42a5f5] text-white hover:bg-[#1976d2] active:bg-[#1565c0]'
                  }`}
                >
                  All Time
                </button>
                <button
                  onClick={() => switchTrackingMode('trip-mode')}
                  className={`px-6 sm:px-4 min-h-[48px] py-3 sm:py-2 rounded-lg font-medium shadow-sm transition-colors text-sm touch-manipulation active:scale-95 flex-1 sm:flex-none ${
                    trackingMode === 'trip-mode'
                      ? 'bg-white text-[#42a5f5]'
                      : 'bg-[#42a5f5] text-white hover:bg-[#1976d2] active:bg-[#1565c0]'
                  }`}
                >
                  Trip
                </button>
              </div>

              {/* Trip Reset Button - Full width on mobile when active, min 48px touch target */}
              {trackingMode === 'trip-mode' && (
                <div className="flex justify-center sm:justify-start">
                  <button
                    onClick={handleResetTrip}
                    className="bg-red-500 text-white px-6 sm:px-4 min-h-[48px] py-3 sm:py-2 rounded-lg font-medium hover:bg-red-600 active:bg-red-700 transition-colors shadow-sm text-sm touch-manipulation active:scale-95 w-full sm:w-auto"
                  >
                    Reset Trip
                  </button>
                </div>
              )}

              {/* Action Buttons - Improved touch targets */}
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3">
                <button
                  onClick={() => setShareDialogOpen(true)}
                  className="bg-white/10 border border-white/20 text-white px-6 sm:px-4 min-h-[48px] py-3 sm:py-2 rounded-lg font-medium hover:bg-white/20 active:bg-white/30 transition-colors shadow-sm text-sm flex items-center justify-center space-x-2 touch-manipulation active:scale-95"
                >
                  <Share2 className="h-5 w-5 sm:h-4 sm:w-4" />
                  <span>Share Progress</span>
                </button>

                {/* User Button Container - Center on mobile */}
                <div className="flex justify-center sm:justify-start min-h-[48px] items-center">
                  <UserButton>
                    <UserButton.MenuItems>
                      <UserButton.Link
                        label="Settings"
                        labelIcon={<Settings className="h-4 w-4" />}
                        href="/settings"
                      />
                    </UserButton.MenuItems>
                  </UserButton>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8 pb-24 md:pb-8">
        {/* Stats Cards - Enhanced for mobile */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6 sm:mb-8">
          <div className="bg-white p-4 sm:p-6 rounded-lg shadow-sm border hover:shadow-md transition-shadow">
            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0 p-2 bg-blue-50 rounded-lg">
                <Trophy className="h-6 w-6 sm:h-7 sm:w-7 text-[#42a5f5]" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-2xl sm:text-3xl font-bold text-[#42a5f5] leading-none">{totalSpots}</p>
                <p className="text-gray-600 text-sm sm:text-sm font-medium mt-1 leading-tight">
                  {trackingMode === 'all-time' ? 'Total Spots' : 'Trip Spots'}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white p-4 sm:p-6 rounded-lg shadow-sm border hover:shadow-md transition-shadow">
            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0 p-2 bg-blue-50 rounded-lg">
                <Award className="h-6 w-6 sm:h-7 sm:w-7 text-[#42a5f5]" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-2xl sm:text-3xl font-bold text-[#42a5f5] leading-none">{uniqueLeagues}</p>
                <p className="text-gray-600 text-sm sm:text-sm font-medium mt-1 leading-tight">Leagues</p>
              </div>
            </div>
          </div>
          <div className="bg-white p-4 sm:p-6 rounded-lg shadow-sm border hover:shadow-md transition-shadow">
            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0 p-2 bg-blue-50 rounded-lg">
                <Users className="h-6 w-6 sm:h-7 sm:w-7 text-[#42a5f5]" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-2xl sm:text-3xl font-bold text-[#42a5f5] leading-none">{availableTeams}</p>
                <p className="text-gray-600 text-sm sm:text-sm font-medium mt-1 leading-tight">Available</p>
              </div>
            </div>
          </div>
          <div className="bg-white p-4 sm:p-6 rounded-lg shadow-sm border hover:shadow-md transition-shadow">
            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0 p-2 bg-blue-50 rounded-lg">
                <TrendingUp className="h-6 w-6 sm:h-7 sm:w-7 text-[#42a5f5]" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-2xl sm:text-3xl font-bold text-[#42a5f5] leading-none">{progress}%</p>
                <p className="text-gray-600 text-sm sm:text-sm font-medium mt-1 leading-tight">Progress</p>
              </div>
            </div>
          </div>
        </div>

        {/* Tab Navigation - Hidden on mobile (using bottom nav), visible on desktop */}
        <div className="hidden md:flex flex-col sm:flex-row sm:items-center sm:justify-between border-b border-gray-200 mb-6">
          <div className="flex space-x-1 sm:space-x-8 mb-4 sm:mb-0 overflow-x-auto">
            <button
              onClick={() => setActiveTab('your-teams')}
              className={`pb-3 px-3 sm:px-1 font-medium text-sm sm:text-base whitespace-nowrap touch-manipulation ${
                activeTab === 'your-teams'
                  ? 'text-[#42a5f5] border-b-2 border-[#42a5f5]'
                  : 'text-gray-500 hover:text-gray-700 active:text-gray-800'
              }`}
            >
              {trackingMode === 'all-time' ? 'Your Teams' : 'Trip Teams'} ({totalSpots})
            </button>
            <button
              onClick={() => setActiveTab('browse')}
              className={`pb-3 px-3 sm:px-1 font-medium text-sm sm:text-base whitespace-nowrap touch-manipulation ${
                activeTab === 'browse'
                  ? 'text-[#42a5f5] border-b-2 border-[#42a5f5]'
                  : 'text-gray-500 hover:text-gray-700 active:text-gray-800'
              }`}
            >
              Browse Teams ({availableTeams})
            </button>
            {trackingMode === 'all-time' && (
              <button
                onClick={() => setActiveTab('leaderboard')}
                className={`pb-3 px-3 sm:px-1 font-medium text-sm sm:text-base whitespace-nowrap touch-manipulation ${
                  activeTab === 'leaderboard'
                    ? 'text-[#42a5f5] border-b-2 border-[#42a5f5]'
                    : 'text-gray-500 hover:text-gray-700 active:text-gray-800'
                }`}
              >
                Groups
              </button>
            )}
          </div>

          {/* Team Suggestion Button - Enhanced for mobile */}
          <button
            onClick={() => setIsTeamSuggestionOpen(true)}
            className="bg-[#42a5f5] text-white px-4 sm:px-4 py-3 sm:py-2 rounded-lg text-sm font-medium hover:bg-[#1976d2] transition-colors flex items-center justify-center space-x-2 mb-6 shadow-sm active:bg-[#1565c0] touch-manipulation w-full sm:w-auto"
          >
            <Plus className="h-5 w-5 sm:h-4 sm:w-4" />
            <span>Suggest Team</span>
          </button>
        </div>

        {/* Mobile Suggest Team Button - Show when bottom nav is present */}
        <div className="md:hidden mb-4">
          <button
            onClick={() => setIsTeamSuggestionOpen(true)}
            className="bg-[#42a5f5] text-white px-6 min-h-[48px] py-3 rounded-lg text-sm font-medium hover:bg-[#1976d2] transition-colors flex items-center justify-center space-x-2 shadow-sm active:bg-[#1565c0] touch-manipulation w-full"
          >
            <Plus className="h-5 w-5" />
            <span>Suggest Team</span>
          </button>
        </div>

        {activeTab === 'your-teams' ? (
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">
                {trackingMode === 'all-time' ? 'Your Spotted Teams' : 'Trip Spotted Teams'}
              </h3>
              <div className="flex items-center space-x-3">
                {spottedTeams.length > 0 && (
                  <div className="flex items-center space-x-2">
                    <Filter className="h-4 w-4 text-gray-500" />
                    <span className="text-sm font-medium text-gray-700">Sort:</span>
                    <select 
                      value={spottedTeamsSortBy} 
                      onChange={(e) => setSpottedTeamsSortBy(e.target.value as 'alphabetical' | 'league' | 'date')}
                      className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#42a5f5] focus:border-[#42a5f5] bg-white"
                    >
                      <option value="alphabetical">Alphabetical (A-Z)</option>
                      <option value="league">By League</option>
                      <option value="date">By Date Spotted</option>
                    </select>
                  </div>
                )}
                {trackingMode === 'trip-mode' && spottedTeams.length > 0 && (
                  <button 
                    onClick={handleResetTrip}
                    className="bg-red-500 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-red-600 transition-colors"
                  >
                    Reset Trip
                  </button>
                )}
              </div>
            </div>
            {spottedTeams.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-500 mb-2">
                  {trackingMode === 'all-time'
                    ? 'No teams spotted yet. Start browsing to spot your first team!'
                    : 'No teams spotted on this trip yet. Start spotting to compete with friends!'
                  }
                </p>
                {trackingMode === 'trip-mode' && (
                  <p className="text-sm text-gray-400">
                    Tip: Share your trip progress with friends and see who can spot the most teams!
                  </p>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 tablet:grid-cols-2 laptop:grid-cols-3 gap-4">
                {sortedSpottedTeams.map((team, index) => (
                  <SwipeableCard
                    key={`${team.id}-${(team as any).mode}-${index}`}
                    onRemove={() => handleRemoveSpot(team)}
                    className="h-full"
                  >
                    <SpottedTeamCard
                      team={team as Team & { spottedAt: Date; mode?: 'all-time' | 'trip-mode' }}
                      spottedAt={(team as any).spottedAt || new Date()}
                      spottedMode={(team as any).mode || trackingMode}
                      onViewDetails={handleTeamClick}
                      onRemove={handleRemoveSpot}
                      isRemoving={false}
                    />
                  </SwipeableCard>
                ))}
              </div>
            )}
          </div>
        ) : activeTab === 'browse' ? (
          <EnhancedTeamBrowser />
        ) : activeTab === 'leaderboard' ? (
          <div className="space-y-6">
            {/* Groups Introduction */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-start space-y-3 sm:space-y-0 sm:space-x-4">
                <div className="flex-shrink-0">
                  <Users className="h-8 w-8 sm:h-6 sm:w-6 text-blue-600" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg sm:text-base font-semibold text-blue-900 mb-2">Your Leaderboard Groups</h3>
                  <p className="text-sm text-blue-700 leading-relaxed mb-4">
                    Join friends in groups to compete and see who spots the most teams. Create a new group or join one using a shared link!
                  </p>
                  
                  {/* Mobile-Optimized Action Buttons */}
                  <div className="flex flex-col sm:flex-row gap-3 sm:gap-2">
                    <button className="bg-[#42a5f5] hover:bg-[#1976d2] text-white px-6 py-3 sm:px-4 sm:py-2 rounded-lg font-medium transition-colors flex items-center justify-center space-x-2 shadow-sm active:bg-[#1565c0] touch-manipulation">
                      <Plus className="h-5 w-5 sm:h-4 sm:w-4" />
                      <span>Create Group</span>
                    </button>
                    <button className="bg-white hover:bg-gray-50 text-[#42a5f5] border-2 border-[#42a5f5]/30 px-6 py-3 sm:px-4 sm:py-2 rounded-lg font-medium transition-colors flex items-center justify-center space-x-2 shadow-sm active:bg-gray-100 touch-manipulation">
                      <Users className="h-5 w-5 sm:h-4 sm:w-4" />
                      <span>Join Group</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Add visual separator */}
            <div className="border-t border-gray-200 pt-6">
              <Leaderboard
                type="all-time"
                currentUserId={user?.id}
                onUserClick={handleViewUser}
              />
            </div>
            
            {/* Don't see your team prompt - Enhanced for mobile */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center space-y-3 sm:space-y-0 sm:space-x-4">
                <div className="flex-1">
                  <h4 className="font-semibold text-amber-900 mb-2">Don't see your team?</h4>
                  <p className="text-sm text-amber-800 mb-3">
                    We're always adding new teams! Suggest yours and we'll review it for inclusion.
                  </p>
                  <a 
                    href="https://sportspot.com/suggest" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg font-medium transition-colors touch-manipulation active:bg-amber-800"
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Suggest Team
                  </a>
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </div>

      {/* Team Detail Modal */}
      <TeamDetailModal
        team={selectedTeam}
        isOpen={isTeamModalOpen}
        onClose={closeTeamModal}
      />

      {/* Team Suggestion Dialog */}
      <TeamSuggestionDialog
        isOpen={isTeamSuggestionOpen}
        onClose={() => setIsTeamSuggestionOpen(false)}
      />

      {/* Remove Team Confirmation Modal */}
      <AlertDialog open={!!teamToRemove} onOpenChange={() => setTeamToRemove(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Team</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove <strong>{teamToRemove?.name}</strong> from your spotted teams? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setTeamToRemove(null)}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmRemoveSpot} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Remove Team
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reset Trip Confirmation Modal */}
      <AlertDialog open={showResetConfirm} onOpenChange={setShowResetConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reset Trip</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to reset all trip spots? This will remove all teams from your current trip and cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setShowResetConfirm(false)}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmResetTrip} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Reset Trip
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Share Dialog */}
      <ShareDialog
        isOpen={shareDialogOpen}
        onClose={() => setShareDialogOpen(false)}
        type="all-time"
      />
    </div>
  )
}